// Sidebar nav model — source of truth.
//
// Mirrors design/UI : UX v1.0/for-engineering-shell/specs/shell-strings.md
// exactly (5 sections × 15 items, each with a `requiredPermission`).
//
// Labels are NOT here — they live in admin/src/i18n/strings.tsx and are
// looked up by nav key. This file is locale-free; the model is the same
// regardless of language.

import { rolesHavePermission, type Permission, type RoleKey } from '@gc-coach/shared';

export type SectionKey =
  | 'overview'
  | 'knowledge'
  | 'insights'
  | 'customers'
  | 'administration';

export type NavItemKey =
  | 'dashboard'
  | 'analytics'
  | 'notifications'
  | 'sources'
  | 'review'
  | 'kb'
  | 'frameworks'
  | 'personae'
  | 'benchmark'
  | 'rag'
  | 'labs'
  | 'tenants'
  | 'moderation'
  | 'billing'
  | 'users'
  | 'audit'
  | 'settings';

export interface NavItem {
  key: NavItemKey;
  /** Permission required to see this item. `null` = visible to all signed-in users (e.g. notifications). */
  requiredPermission: Permission | null;
  /** Route this nav item navigates to. */
  to: string;
}

export interface NavSection {
  key: SectionKey;
  items: ReadonlyArray<NavItem>;
}

export const NAV_MODEL: ReadonlyArray<NavSection> = [
  {
    key: 'overview',
    items: [
      { key: 'dashboard', requiredPermission: 'analytics:read', to: '/admin/dashboard' },
      { key: 'analytics', requiredPermission: 'analytics:read', to: '/admin/analytics' },
      { key: 'notifications', requiredPermission: null, to: '/admin/notifications' },
    ],
  },
  {
    key: 'knowledge',
    items: [
      { key: 'sources', requiredPermission: 'sources:write', to: '/admin/knowledge/sources' },
      { key: 'review', requiredPermission: 'kb:write', to: '/admin/knowledge/review' },
      { key: 'kb', requiredPermission: 'kb:read', to: '/admin/knowledge/base' },
      { key: 'frameworks', requiredPermission: 'frameworks:write', to: '/admin/knowledge/frameworks' },
      { key: 'personae', requiredPermission: 'kb:write', to: '/admin/knowledge/personae' },
    ],
  },
  {
    key: 'insights',
    items: [
      { key: 'benchmark', requiredPermission: 'benchmark:read', to: '/admin/insights/benchmark' },
      { key: 'rag', requiredPermission: 'kb:write', to: '/admin/insights/rag' },
      { key: 'labs', requiredPermission: 'kb:write', to: '/admin/insights/labs' },
    ],
  },
  {
    key: 'customers',
    items: [
      { key: 'tenants', requiredPermission: 'tenants:write', to: '/admin/customers/tenants' },
      { key: 'moderation', requiredPermission: 'moderation:read', to: '/admin/customers/moderation' },
      { key: 'billing', requiredPermission: 'billing:write', to: '/admin/customers/billing' },
    ],
  },
  {
    key: 'administration',
    items: [
      { key: 'users', requiredPermission: 'users:read', to: '/admin/team/users' },
      { key: 'audit', requiredPermission: 'audit:read', to: '/admin/admin/audit' },
      { key: 'settings', requiredPermission: 'settings:read', to: '/admin/admin/settings' },
    ],
  },
];

/**
 * Filter the nav model down to what a given role-set is permitted to see.
 * Sections with zero visible items are dropped. **Convenience filter only**,
 * not a security boundary — the backend authorizes every request regardless
 * (per shell-strings.md §"RBAC nav visibility rules").
 */
export function visibleNav(roles: ReadonlyArray<RoleKey>): NavSection[] {
  return NAV_MODEL.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        item.requiredPermission === null || rolesHavePermission(roles, item.requiredPermission),
    ),
  })).filter((section) => section.items.length > 0);
}

/** Lookup helper — find an item's route by its key. Used for default routing. */
export function navItemRoute(key: NavItemKey): string | undefined {
  for (const section of NAV_MODEL) {
    const found = section.items.find((i) => i.key === key);
    if (found) return found.to;
  }
  return undefined;
}
